import { createElement as rc, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import _Range from '../../_abstractComponent/Range';
import useDbView from '../../../hooks/useDbView';
import { contexts, styled, View, hooks } from 'lib_ui-primitives';
import { constants, filters, metadata } from 'lib_ui-services';

const { useBbState } = hooks;
const { SCOPE, VISIBILITY, DURATION } = constants.retention;
const PER_ROUTE = {
    scope: SCOPE.PER_ROUTE,
    duration: DURATION.PAGE_LOAD,
    visibility: VISIBILITY.PER_BROWSER
};

const RangeSearchElement = styled(View).attrs({ name: 'range-search-element' })`
    overflow: visible;
`;
RangeSearchElement.displayName = 'RangeSearchElement';

const rangeTypes = {
    DateTime: { filter: 'dateRange', childComponentType: 'DatePicker' },
    Number: { filter: 'numberRange', childComponentType: 'TextInput', childComponentProps: { type: 'number' } }
};

const _p = { useDbView, useBbState };
export const _private = _p;
export default function Range(props) {
    const {
        hNode,
        hNode: { propertyName, id, fromInputHNode, filters: { namespace = '__none', relation = '__none' } = {} },
        filterWrapper = x => x
    } = props ?? { hNode: {} };

    const filterType = rangeTypes[fromInputHNode.hNodeType].filter;

    // Get the default range if available
    const defaultFilters = useMemo(() => filters.filterHelpers.getDefaultFilters(hNode), [hNode]);
    const defaultFromValue = defaultFilters?.[filterType]?.fromValue;
    const defaultToValue = defaultFilters?.[filterType]?.toValue;

    // If a default range has been set, use that as the initial value, otherwise fallback to an empty string.
    // The empty string (rather than the previous undefined) prevents an error when changing the value from undefined to defined.
    // On navigation to a page with a default date range, the to/from dates will be displayed on the date picker, and the grid will be filtered.
    const [fromValue, setFromValue] = _p.useBbState(defaultFromValue ?? '', `from-${propertyName}-${id}`, PER_ROUTE);
    const [toValue, setToValue] = _p.useBbState(defaultToValue ?? '', `to-${propertyName}-${id}`, PER_ROUTE);

    const propertyPath = metadata.getPathToProperty(hNode);

    const { viewCriteria } = _p.useDbView(namespace, relation, undefined, hNode);
    const valueChanged = useContext(contexts.SearchContext);
    useEffect(() => {
        if (!viewCriteria) return;
        const filter = {
            [filterType]: filters[filterType].getFilter(fromValue, toValue, propertyPath, fromInputHNode.format, true)
        };

        // Set the filters for the viewCriteria so that there isn't a delay in filtering the grid on navigation when a default date range is set.
        viewCriteria.applyFilters(
            {
                ...defaultFilters,
                // somehow the filterWrapper should be incorporated here, but not sure how...
                // hoping Range won't be used nested, with a default?
                ...filter
            },
            id
        );

        // SearchPane uses a `searchCriteria` filter, so it needs the mongodb syntax
        // Passing in `filters` as I can't overcome recursion and circular dependencies in a different way for nestedListEntry... :(
        const mongoSyntax = filterWrapper(filters[filterType].getMql(filter));
        valueChanged(hNode, mongoSyntax);
    }, [
        valueChanged,
        hNode,
        propertyPath,
        fromValue,
        toValue,
        fromInputHNode.format,
        filterType,
        id,
        viewCriteria,
        defaultFilters,
        filterWrapper
    ]);

    return rc(
        RangeSearchElement,
        null,
        rc(_Range, {
            ...props,
            fromValue,
            setFromValue,
            toValue,
            setToValue,
            rangeTypes
        })
    );
}

Range.propTypes = {
    hNode: PropTypes.shape({
        propertyName: PropTypes.string,
        id: PropTypes.string.isRequired,
        fromInputHNode: PropTypes.shape({
            hNodeType: PropTypes.string.isRequired,
            format: PropTypes.string
        }).isRequired
    }).isRequired
};
